import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Badge, Button, Container, Select, Text, Alert } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';

import { GET_MANUAL_IMPORT_LOGS } from '../../../apollo/queries/getManualImportLogs';
import {
    SURVEY_TYPE_LABELS,
    SURVEY_TYPE_OPTIONS,
    SurveyType,
} from '../../../constants/surveyTypes';
import { ImportType } from '../../../types/import';
import ManualImportErrorCell from '../../../components/file-uploads/ManualImportErrorCell';
import ManualImportFileCell from '../../../components/file-uploads/ManualImportFileCell';
import When from '../../../components/common/When';
import { getErrorMessage } from '../../../helpers/getErrorMessage';
import { useUser } from '../../../hooks/useUser';

const STATUS_COLORS: Record<string, string> = {
    Completed: 'green',
    Failed: 'red',
    Processing: 'blue',
    Pending: 'gray',
};

type UploadHistoryRow = {
    id: string;
    surveyType?: string;
    label?: string;
    fileName: string;
    s3Key?: string | null;
    status: string;
    recordsProcessed?: number | null;
    error?: string | null;
    createdBy?: {
        firstName?: string;
        lastName?: string;
    } | null;
    completedAt?: string | null;
    createdAt: string;
};

type SurveyUploadHistoryTableProps = {
    refreshKey: number;
};

function SurveyUploadHistoryTable({
    refreshKey,
}: SurveyUploadHistoryTableProps) {
    const { isAdmin, isSuperAdmin, isProcurementManager, isDivisionManager } =
        useUser();
    const canDownload =
        isAdmin || isSuperAdmin || isProcurementManager || isDivisionManager;
    const [surveyTypeFilter, setSurveyTypeFilter] = useState<
        SurveyType | 'ALL'
    >('ALL');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    const surveyTypeFilterData = useMemo(
        () => [
            { value: 'ALL', label: 'All survey types' },
            ...SURVEY_TYPE_OPTIONS.map(o => ({
                value: o.value,
                label: o.label,
            })),
        ],
        [],
    );

    const importTypes = useMemo(() => {
        if (surveyTypeFilter === 'ALL') {
            return [
                ImportType.Top20,
                ImportType.CategorySurvey,
                ImportType.SpecialProjectSurvey,
            ];
        }
        const option = SURVEY_TYPE_OPTIONS.find(
            o => o.value === surveyTypeFilter,
        );
        if (!option) return [ImportType.Top20];
        const map: Record<SurveyType, ImportType> = {
            [SurveyType.Top20]: ImportType.Top20,
            [SurveyType.Category]: ImportType.CategorySurvey,
            [SurveyType.SpecialProject]: ImportType.SpecialProjectSurvey,
        };
        return [map[surveyTypeFilter]];
    }, [surveyTypeFilter]);

    const { data, loading, error, refetch } = useQuery(GET_MANUAL_IMPORT_LOGS, {
        variables: {
            input: {
                importTypes,
                statuses: statusFilter ? [statusFilter] : undefined,
                limit: 50,
                offset: 0,
            },
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    React.useEffect(() => {
        refetch();
    }, [refreshKey, refetch]);

    const rows: UploadHistoryRow[] = data?.getManualImportLogs?.logs ?? [];

    return (
        <Container size="xl" className="rounded-lg p-4 mt-8 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <Text size="lg" fw={600}>
                    Upload history
                </Text>
                <div className="flex flex-wrap items-center gap-3">
                    <Select
                        placeholder="All statuses"
                        clearable
                        data={[
                            { value: 'Completed', label: 'Completed' },
                            { value: 'Failed', label: 'Failed' },
                            { value: 'Processing', label: 'Processing' },
                            { value: 'Pending', label: 'Pending' },
                        ]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        className="w-48"
                    />
                    <Select
                        placeholder="All survey types"
                        data={surveyTypeFilterData}
                        value={surveyTypeFilter}
                        onChange={value =>
                            setSurveyTypeFilter(
                                (value as SurveyType | 'ALL') || 'ALL',
                            )
                        }
                        className="w-48"
                    />
                </div>
            </div>
            <When condition={Boolean(error)}>
                <Alert color="red" variant="light" className="mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <Text size="sm">{getErrorMessage(error)}</Text>
                        <Button
                            size="xs"
                            variant="light"
                            onClick={() => refetch()}
                        >
                            Retry
                        </Button>
                    </div>
                </Alert>
            </When>
            <DataTable<UploadHistoryRow>
                withTableBorder
                borderRadius="md"
                striped
                highlightOnHover
                minHeight={200}
                fetching={loading}
                records={rows}
                idAccessor="id"
                noRecordsText={
                    error ? 'Unable to load uploads' : 'No uploads found'
                }
                columns={[
                    {
                        accessor: 'surveyType',
                        title: 'Survey type',
                        render: row =>
                            row.surveyType
                                ? SURVEY_TYPE_LABELS[
                                      row.surveyType as SurveyType
                                  ] || row.surveyType
                                : '-',
                    },
                    {
                        accessor: 'label',
                        title: 'Label',
                        render: row => row.label || '-',
                    },
                    {
                        accessor: 'fileName',
                        title: 'File',
                        width: 280,
                        noWrap: true,
                        render: row => (
                            <ManualImportFileCell
                                logId={row.id}
                                fileName={row.fileName}
                                s3Key={row.s3Key}
                                canDownload={canDownload}
                            />
                        ),
                    },
                    {
                        accessor: 'status',
                        title: 'Status',
                        width: 140,
                        render: row => (
                            <Badge
                                color={STATUS_COLORS[row.status] || 'gray'}
                                variant="light"
                            >
                                {row.status}
                            </Badge>
                        ),
                    },
                    {
                        accessor: 'recordsProcessed',
                        title: 'Records',
                        render: row => row.recordsProcessed ?? '-',
                    },
                    {
                        accessor: 'error',
                        title: 'Error',
                        width: 160,
                        render: row => (
                            <ManualImportErrorCell
                                logId={row.id}
                                status={row.status}
                                error={row.error}
                            />
                        ),
                    },
                    {
                        accessor: 'createdBy',
                        title: 'Uploaded by',
                        render: row =>
                            row.createdBy
                                ? `${row.createdBy.firstName} ${row.createdBy.lastName}`
                                : '-',
                    },
                    {
                        accessor: 'completedAt',
                        title: 'Date',
                        render: row =>
                            dayjs(row.completedAt || row.createdAt).format(
                                'YYYY-MM-DD HH:mm',
                            ),
                    },
                ]}
            />
        </Container>
    );
}

export default SurveyUploadHistoryTable;
